import { IkeFieldType } from '@ikegps/core-api';
import type { CollectionModel } from '../collection.data';
import type { CollectionModelBase } from '../collection.base';
import type { CollectionModelField } from '../collection.field';
import { CollectionModelFieldSubForm } from '../fields/form.field';
import type { FormModel } from '../../form/form';
import type { FormModelField } from '../../form/form.field';
import { IkeModelCollection } from '../collection.model';
import { Log } from '../../../log';
import { FormModelFieldSubForm } from '../../form/subform/form.subform';
import { CollectionModelFieldSubFormInstance } from '../collection.base';
import {
	type IkeCollectionMigrationAction,
	IkeCollectionUpgradeActionFactory,
	type IkeCollectionUpgradeRunner,
} from './collection.upgrade.action';

export type IkeCollectionUpgradeFailure = IkeCollectionMigrationUpgradeMissing | IkeCollectionUpgradeFailureReplace;

/** Field is missing in target collection. */
export interface IkeCollectionMigrationUpgradeMissing {
	fieldId: string;
	fieldName?: string;
	reason: 'missing';
}

/** Field found in target, but no rule was found to map value. */
export interface IkeCollectionUpgradeFailureReplace {
	fieldId: string;
	fieldName?: string;
	reason: 'replace';
	values: string[];
}

export class IkeCollectionUpgrade {
	targetForm: FormModel;
	collections: CollectionModel[];

	validations: { [fieldId: string]: IkeCollectionUpgradeFailure } = {};
	actions: { [fieldId: string]: IkeCollectionUpgradeRunner } = {};

	constructor(collections: CollectionModel[], targetForm: FormModel) {
		this.collections = collections;
		this.targetForm = targetForm;
	}

	/** Add a migration action for a field, eg drop, replace */
	addAction(action: IkeCollectionMigrationAction): this {
		if (this.actions[action.fieldId]) {
			throw new Error('Duplicate action fieldId: ' + action.fieldId);
		}
		this.actions[action.fieldId] = IkeCollectionUpgradeActionFactory.createAction(action);
		return this;
	}

	async process(log: Log) {
		const output: CollectionModel[] = [];

		for (const oldCollection of this.collections) {
			const modelCollection = new IkeModelCollection();
			const newCollection = modelCollection.read(
				await modelCollection.write(oldCollection),
				this.targetForm,
				oldCollection.loader
			);
			await newCollection.ready();

			await this.processSubForm(oldCollection, newCollection, log.child({ collectionId: oldCollection.id }));

			output.push(newCollection);
		}
		const validations = Object.keys(this.validations);
		if (validations.length > 0) {
			return { result: 'failure' as const, fields: validations.map((c) => this.validations[c]!) };
		}
		return { result: 'success' as const, collections: output };
	}

	private findMatchingField(
		field: FormModelField,
		oldSubForm: FormModelFieldSubForm,
		newSubForm: FormModelFieldSubForm
	): string {
		// If the action maps the field then use that
		const action = this.actions[field.id];
		if (action != null) {
			return action.targetFieldId;
		}

		// Attempt to look for a matching field by display name

		const sourceMatches = oldSubForm.fields.filter((f) => f.displayName === field.displayName && f.type === field.type);
		if (sourceMatches.length > 1) {
			// Multiple source fields, skip
			return null;
		}

		const matches = newSubForm.fields.filter((f) => f.displayName === field.displayName && f.type === field.type);
		if (matches.length !== 1) {
			// Multiple target fields, skip
			return null;
		}

		return matches[0].id;
	}

	private getAction(oldField: CollectionModelField) {
		return this.actions[oldField.schema.id];
	}

	private async processSubForm(
		oldSubForm: CollectionModelBase,
		newSubForm: CollectionModelBase,
		log: Log
	): Promise<IkeCollectionUpgradeFailure[]> {
		let failures: IkeCollectionUpgradeFailure[] = [];

		for (const schemaField of oldSubForm.schema.fields) {
			const oldField = oldSubForm.getField(schemaField.id);
			if (!oldField.hasValue()) {
				continue;
			}

			// handle undefined yes/no field
			if (oldField.schema.type === IkeFieldType.YES_NO && oldField.displayValue === 'undefined') {
				continue;
			}

			let targetFieldId = schemaField.id;

			const action = this.getAction(oldField);

			// Handle 'drop' as a special case when target form does not have field defined
			if (action != null && !action.requiresTargetField) {
				if (newSubForm.hasField(targetFieldId)) {
					const newDropField = newSubForm.getField(targetFieldId);
					const isActionProcessed = action.process(oldField, newDropField, log);
					if (isActionProcessed) {
						log.trace(
							{ oldFieldId: oldField.id, newFieldId: newDropField.id, action: action.reason },
							'Applied action'
						);
					}
				}

				continue;
			}

			// Attempt to find the mapping for a missed field
			if (!newSubForm.schema.hasField(schemaField.id)) {
				targetFieldId = this.findMatchingField(schemaField, oldSubForm.schema, newSubForm.schema);

				if (targetFieldId == null) {
					log.warn({ fieldId: schemaField.id }, 'Unable to find field mapping');
					this.validations[schemaField.id] = {
						reason: 'missing',
						fieldId: schemaField.id,
						fieldName: schemaField.displayName,
					};
					continue;
				}

				if (oldField.schema.type !== IkeFieldType.FORM) {
					// Form duplication will handle recursively to apply rules at each level
					const targetField = newSubForm.getField(targetFieldId);
					targetField.Mutator.copy(oldField);
				}
			}

			const newField = newSubForm.getField(targetFieldId);

			// field types have changed... TODO handle this
			if (newField.schema.type !== oldField.schema.type) {
				log.error(
					{
						oldFieldId: schemaField.id,
						newFieldId: newField.id,
						oldType: oldField.schema.type,
						newType: newField.schema.type,
					},
					'Mapping invalid types'
				);
				throw new Error('Cannot map between types');
			}

			// Duplicate a sub form
			if (
				CollectionModelFieldSubForm.isSubFormField(oldField) &&
				CollectionModelFieldSubForm.isSubFormField(newField)
			) {
				for (const oldInstance of oldField.value) {
					const newInstance = this.getOrCreateSubformInstance(newField, oldInstance.instanceId);
					const res = await this.processSubForm(oldInstance, newInstance, log);
					if (res.length > 0) {
						failures = failures.concat(res);
					}
				}
			}

			if (action != null && action.process != null) {
				const isActionProcessed = action.process(oldField, newField, log);
				if (isActionProcessed) {
					log.trace({ oldFieldId: oldField.id, newFieldId: newField.id, action: action.reason }, 'Applied action');
					continue;
				}
			}

			if (newField.displayValue !== oldField.displayValue) {
				// Exception for IPF form upgrades: only if oldFormVersion < 3 and newFormVersion >= 3
				// and the field is serviceCommunicationsCable or servicePowerConductor
				const oldFormVersion = oldSubForm.schema.getForm().metadata.getIkePoleForeman?.()?.formVersion ?? 1;
				const newFormVersion = this.targetForm?.metadata?.getIkePoleForeman?.()?.formVersion;
				const isIPFUpgrade =
					(oldField.schema.id === 'ipf_serviceCommunicationsCable' ||
						oldField.schema.id === 'ipf_servicePowerConductor') &&
					newFormVersion !== undefined &&
					oldFormVersion < 3 &&
					newFormVersion >= 3;
				if (isIPFUpgrade) {
					newField.Mutator.truncatedCopy(oldField);
				} else {
					log.error(
						{
							oldFieldId: schemaField.id,
							oldDisplay: oldField.displayValue,
							newDisplay: newField.displayValue,
						},
						'Display miss match'
					);
					this.addReplaceValue(schemaField, oldField.displayValue);
				}
			}
		}
		return failures;
	}

	private getOrCreateSubformInstance(
		subform: CollectionModelFieldSubForm,
		instanceId: string
	): CollectionModelFieldSubFormInstance {
		const newInstance = subform.getInstance(instanceId);
		if (newInstance != null) {
			return newInstance;
		}

		subform.Mutator.create(instanceId);
		return subform.getInstance(instanceId);
	}

	private addReplaceValue(field: FormModelField, value: string) {
		const existing = this.validations[field.id] || {
			fieldId: field.id,
			fieldName: field.displayName,
			reason: 'replace',
			values: [],
		};
		if (existing.reason !== 'replace') {
			throw new Error('Missing and Replace: ' + field.id);
		}
		if (existing.values.indexOf(value) > -1) {
			return;
		}
		existing.values.push(value);
		this.validations[field.id] = existing;
	}
}
